import React, { useState, useRef, useEffect } from 'react'
import { toast } from 'react-toastify'
import TypeSelector from './TypeSelector'

const US_STATES = [
	{ abbr: 'AL', name: 'Alabama' },
	{ abbr: 'AK', name: 'Alaska' },
	{ abbr: 'AZ', name: 'Arizona' },
	{ abbr: 'AR', name: 'Arkansas' },
	{ abbr: 'CA', name: 'California' },
	{ abbr: 'CO', name: 'Colorado' },
	{ abbr: 'CT', name: 'Connecticut' },
	{ abbr: 'DE', name: 'Delaware' },
	{ abbr: 'FL', name: 'Florida' },
	{ abbr: 'GA', name: 'Georgia' },
	{ abbr: 'HI', name: 'Hawaii' },
	{ abbr: 'ID', name: 'Idaho' },
	{ abbr: 'IL', name: 'Illinois' },
	{ abbr: 'IN', name: 'Indiana' },
	{ abbr: 'IA', name: 'Iowa' },
	{ abbr: 'KS', name: 'Kansas' },
	{ abbr: 'KY', name: 'Kentucky' },
	{ abbr: 'LA', name: 'Louisiana' },
	{ abbr: 'ME', name: 'Maine' },
	{ abbr: 'MD', name: 'Maryland' },
	{ abbr: 'MA', name: 'Massachusetts' },
	{ abbr: 'MI', name: 'Michigan' },
	{ abbr: 'MN', name: 'Minnesota' },
	{ abbr: 'MS', name: 'Mississippi' },
	{ abbr: 'MO', name: 'Missouri' },
	{ abbr: 'MT', name: 'Montana' },
	{ abbr: 'NE', name: 'Nebraska' },
	{ abbr: 'NV', name: 'Nevada' },
	{ abbr: 'NH', name: 'New Hampshire' },
	{ abbr: 'NJ', name: 'New Jersey' },
	{ abbr: 'NM', name: 'New Mexico' },
	{ abbr: 'NY', name: 'New York' },
	{ abbr: 'NC', name: 'North Carolina' },
	{ abbr: 'ND', name: 'North Dakota' },
	{ abbr: 'OH', name: 'Ohio' },
	{ abbr: 'OK', name: 'Oklahoma' },
	{ abbr: 'OR', name: 'Oregon' },
	{ abbr: 'PA', name: 'Pennsylvania' },
	{ abbr: 'RI', name: 'Rhode Island' },
	{ abbr: 'SC', name: 'South Carolina' },
	{ abbr: 'SD', name: 'South Dakota' },
	{ abbr: 'TN', name: 'Tennessee' },
	{ abbr: 'TX', name: 'Texas' },
	{ abbr: 'UT', name: 'Utah' },
	{ abbr: 'VT', name: 'Vermont' },
	{ abbr: 'VA', name: 'Virginia' },
	{ abbr: 'WA', name: 'Washington' },
	{ abbr: 'WV', name: 'West Virginia' },
	{ abbr: 'WI', name: 'Wisconsin' },
	{ abbr: 'WY', name: 'Wyoming' },
]

export default function StateDestinationFilter({
	filters,
	setFilters,
	labels,
	styles,
	isDarkMode,
	destinationType,
	setDestinationType,
}) {
	const [inputValue, setInputValue] = useState('')
	const [selectedStates, setSelectedStates] = useState(
		filters.destinationStates || []
	)
	const [showDropdown, setShowDropdown] = useState(false)
	const [filteredStates, setFilteredStates] = useState([])
	const [isFocused, setIsFocused] = useState(false)
	const dropdownRef = useRef(null)
	const inputRef = useRef(null)

	// Sync with external filter changes (e.g., when X button is clicked or Clear Filters)
	useEffect(() => {
		setSelectedStates(filters.destinationStates || [])
	}, [filters.destinationStates])

	useEffect(() => {
		const handleDocumentClick = event => {
			if (dropdownRef.current && !dropdownRef.current.contains(event.target)) {
				setShowDropdown(false)
			}
		}

		document.addEventListener('mousedown', handleDocumentClick)
		return () => {
			document.removeEventListener('mousedown', handleDocumentClick)
		}
	}, [])

	const handleInputChange = e => {
		const value = e.target.value
		setInputValue(value)

		if (value.trim()) {
			const filtered = US_STATES.filter(
				state =>
					state.name.toLowerCase().includes(value.toLowerCase()) ||
					state.abbr.toLowerCase().includes(value.toLowerCase())
			)
			setFilteredStates(filtered)
			setShowDropdown(true)
		} else {
			// Show all states when input is empty but focused
			setFilteredStates(US_STATES)
			setShowDropdown(true)
		}
	}

	const handleStateToggle = state => {
		const isSelected = selectedStates.includes(state.abbr)
		const newStates = isSelected
			? selectedStates.filter(s => s !== state.abbr)
			: [...selectedStates, state.abbr]

		// Check limit when adding new state
		if (!isSelected && newStates.length > 10) {
			toast.warning('Maximum 10 states allowed')
			// Keep focus on input even when limit is reached
			if (inputRef.current) {
				inputRef.current.focus()
			}
			return
		}

		setSelectedStates(newStates)
		setFilters(prev => ({
			...prev,
			destinationStates: newStates,
			paginateSize: 1,
		}))
		setInputValue('') // Clear input after selection

		// Keep focus on the input after selection
		if (inputRef.current) {
			inputRef.current.focus()
		}

		// Reset filtered states to show all
		setFilteredStates(US_STATES)
	}

	const inputStyles = {
		...styles,
		height: '40px',
		boxSizing: 'border-box',
		fontSize: '14px',
		borderTopLeftRadius: '0',
		borderBottomLeftRadius: '0',
		borderTopRightRadius: '6px',
		borderBottomRightRadius: '6px',
		outline: 'none',
		border: `1px solid ${isDarkMode ? '#404040' : '#cccccc'}`,
	}

	const inputClassName = `input join-item shrink mt-1 p-2 w-full focus:ring-0 focus:outline-none`

	return (
		<div className="join flex flex-1" ref={dropdownRef}>
			<TypeSelector
				destinationType={destinationType}
				setDestinationType={setDestinationType}
				labels={labels}
				styles={styles}
				isDarkMode={isDarkMode}
			/>

			{/* State Selector */}
			<div className="input-container flex-grow relative">
				<input
					ref={inputRef}
					type="text"
					id="state-input"
					className={`${inputClassName} join-item z-60`}
					style={inputStyles}
					value={
						isFocused ? inputValue : selectedStates.join(', ') || inputValue
					}
					onChange={handleInputChange}
					onFocus={() => {
						setIsFocused(true)
						setInputValue('')
						if (!showDropdown) {
							setFilteredStates(US_STATES)
							setShowDropdown(true)
						}
					}}
					onBlur={() => {
						setIsFocused(false)

						setInputValue('')
					}}
					autoComplete="off"
				/>
				<label
					htmlFor="state-input"
					className={selectedStates.length > 0 || inputValue ? 'filled' : ''}
					style={{ ...labels, backgroundColor: isDarkMode ? '#1c2126' : '#ffffff',
							color: isDarkMode ? '#C9D1D9' : '#3a4353' }}
					onClick={e => {
						const container = e.target.closest('.input-container')
						if (container) {
							const inputElement = container.querySelector('input')
							if (inputElement) {
								inputElement.focus()
							}
						}
					}}
				>
					State{' '}
					<span style={{ fontSize: '0.99em', opacity: 0.7 }}>(up to 10)</span>
				</label>
				{showDropdown && filteredStates.length > 0 && (
					<div
						className="absolute left-0 right-0 bg-white border shadow-lg z-50"
						style={{
							top: '52px',
							backgroundColor: isDarkMode ? '#1a1a1a' : 'white',
							borderColor: isDarkMode ? '#404040' : '#cccccc',
							borderRadius: '12px',
							boxShadow: isDarkMode
								? '0 12px 28px rgba(0, 0, 0, 0.32), 0 2px 8px rgba(0, 0, 0, 0.2)'
								: '0 10px 24px rgba(0, 0, 0, 0.1), 0 2px 8px rgba(0, 0, 0, 0.06)',
							maxHeight: '280px',
							overflowY: 'auto',
						}}
					>
						{filteredStates
							.sort((a, b) => {
								// Sort selected states to the top
								const aSelected = selectedStates.includes(a.abbr)
								const bSelected = selectedStates.includes(b.abbr)
								if (aSelected && !bSelected) return -1
								if (!aSelected && bSelected) return 1
								// Keep alphabetical order for the rest
								return a.name.localeCompare(b.name)
							})
							.map(state => {
								const isSelected = selectedStates.includes(state.abbr)
								return (
									<div
										key={state.abbr}
										onMouseDown={e => {
											e.preventDefault()
											handleStateToggle(state)
										}}
										className="px-3 cursor-pointer text-sm flex items-center justify-between pb-1"
										style={{
											backgroundColor: isSelected ? '#3b82f6' : 'transparent',
											color: isSelected
												? 'white'
												: isDarkMode
												? 'white'
												: '#000000',
											borderRadius: 0,
										}}
										onMouseEnter={e => {
											if (!isSelected) {
												e.currentTarget.style.backgroundColor = isDarkMode
													? '#374151'
													: '#f3f4f6'
											}
										}}
										onMouseLeave={e => {
											if (!isSelected) {
												e.currentTarget.style.backgroundColor = 'transparent'
											}
										}}
									>
										<span>
											<span className="font-medium">{state.abbr}</span> -{' '}
											{state.name}
										</span>
										{isSelected && (
											<svg
												className="h-4 w-4"
												fill="none"
												stroke="currentColor"
												viewBox="0 0 24 24"
											>
												<path
													strokeLinecap="round"
													strokeLinejoin="round"
													strokeWidth={2}
													d="M5 13l4 4L19 7"
												/>
											</svg>
										)}
									</div>
								)
							})}
					</div>
				)}
			</div>
		</div>
	)
}
